import React, { useCallback } from 'react'
import { useExperiment } from '@optimizely/react-sdk'
import { When } from 'react-if'
import { LinkButton } from '@/atoms/Button'
import { FilledTicketIcon } from '@/atoms/Icons/FilledTicketIcon'
import { HeadingSM, LabelLG, TitleSM } from '@/atoms/Text'
import { AngelDialog } from '@/molecules/AngelDialog'
import { EmbeddedVideo } from '@/molecules/EmbeddedVideo'
import { useLocale } from '@/utils/LocaleUtil'
import { isValidDate } from '@/utils/date-utils'
import { TranslateFunction, useTranslate } from '@/utils/translate/translate-client'
import { formatShowtimesUrl } from '@/views/TicketsLandingView/transforms'
import { TitleRelease } from '@/views/TicketsLandingView/types'

export interface TrailerModalProps {
  show: boolean
  onClose: () => void
  titleRelease: TitleRelease
}

export const TrailerModal: React.FC<TrailerModalProps> = ({ show, onClose, titleRelease }) => {
  const { t } = useTranslate('tickets')
  const experiment = useExperiment('sneak_peek_tickets_landing_page_experiment')
  const experimentOn = experiment[0] === 'sneak_peek'
  const { locale } = useLocale()
  const isCabrini = Boolean(titleRelease?.release?.filmDetails?.filmName?.toLowerCase() === 'cabrini')
  const sneakPeakUrl = 'https://www.youtube.com/embed/__zaqjj2UFw?autoplay=1&t=1'

  const handleClose = useCallback(() => {
    onClose?.()
  }, [onClose])

  const premieringOn = formatPremieringOn(t, locale, titleRelease)

  // TODO: this needs to use the new Theatrics backend once available
  const isInTheaters = titleRelease?.release?.releaseStatus === 'in-theaters'

  const trailerUrl =
    isCabrini && experimentOn
      ? sneakPeakUrl
      : titleRelease?.release?.filmDetails?.trailerSourceUrl
      ? `${titleRelease?.release?.filmDetails?.trailerSourceUrl}?autoplay=1`
      : null
  const ticketsUrl = formatShowtimesUrl(titleRelease.title)

  if (!trailerUrl) return null

  return (
    <When condition={trailerUrl}>
      <AngelDialog
        open={show}
        onClose={handleClose}
        className="bg-black/75 backdrop-blur-xl"
        bodyClassName="min-w-[80vw] lg:min-w-[55vw] bg-core-gray-950 pb-8 w-full lg:p-11"
      >
        <div className="flex flex-col items-center gap-y-11 md:gap-y-16 lg:flex-row lg:gap-x-16 lg:gap-y-0">
          <div className="h-full w-full">
            <EmbeddedVideo url={trailerUrl} className="w-full" iframeStyle={{ borderRadius: '16px' }} />
          </div>
          <div className="flex flex-col gap-y-8">
            <div className="flex flex-col gap-y-4 lg:gap-y-2">
              <HeadingSM className="photon-core-heading-lg whitespace-nowrap" weight="bold">
                {t('watchInTheaters', 'Watch in Theaters')}
              </HeadingSM>
              <TitleSM className="whitespace-nowrap">{premieringOn}</TitleSM>
            </div>

            {isInTheaters && (
              <LinkButton
                onClick={handleClose}
                href={ticketsUrl}
                className="flex w-fit gap-x-1.5 border-0 !bg-oxide-bright px-8 py-3.5 !text-white focus:!bg-oxide-dark hover:!bg-oxide-dark active:!bg-oxide-dark"
                variant="white"
              >
                <FilledTicketIcon color="core-gray-950" size={28} fill={true} />
                <LabelLG color="core-gray-950">{t('getShowtimes', 'Get Showtimes')}</LabelLG>
              </LinkButton>
            )}
          </div>
        </div>
      </AngelDialog>
    </When>
  )
}

export function formatPremieringOn(
  t: TranslateFunction,
  locale: string,
  titleRelease: TitleRelease,
): string | undefined {
  if (!titleRelease) return

  const originalReleaseDate = titleRelease?.title?.originalReleaseDate
    ? new Date(titleRelease.title.originalReleaseDate)
    : null

  if (originalReleaseDate) {
    if (originalReleaseDate < new Date()) return t('inTheatersNow', 'In Theaters Now')

    const releaseDate = originalReleaseDate.toLocaleDateString(locale, {
      month: 'long',
      day: 'numeric',
    })

    return t('premieringOn', 'Premiering On {{releaseDate}}', {
      releaseDate,
    })
  }

  return formatForTheatricalReleaseDate(t, locale, titleRelease)
}

function formatForTheatricalReleaseDate(
  t: TranslateFunction,
  locale: string,
  titleRelease: TitleRelease,
): string | undefined {
  if (titleRelease.release?.dateDisplayOption === 'full-date') {
    const releaseDate = titleRelease.release?.filmDetails?.theatricalReleaseDate
    if (!releaseDate) return

    const date = new Date(releaseDate)
    if (!isValidDate(date)) return

    if (date < new Date()) return t('inTheatersNow', 'In Theaters Now')

    const formatted = date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    })
    return t('premieringOn', 'Premiering On {{releaseDate}}', { releaseDate: formatted })
    // NOTE: month-and-year included only for ease of migration. Current design/titles don't use it
  } else if (
    titleRelease.release?.dateDisplayOption === 'year' ||
    titleRelease.release?.dateDisplayOption === 'month-and-year'
  ) {
    const year = titleRelease.release.filmDetails?.releaseYear
    return year ? `${t('comingIn', 'Coming in')} ${year}` : t('comingSoon', 'Coming Soon')
  }
}
